/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// occupancy.js — the app-wide collision authority: ONE hole holds at most one
// lead, whether it's a chip pin or a wire end. buildOccupancy() derives an
// address → occupant index from a desk document; chip and wire placement
// legality both check against it — never a second bookkeeping structure.
//
// Pure and DOM-free. Pin positions are always DERIVED (footprint + anchor),
// never stored.
//
// A part is always seated on ONE board. A chip and a LINEAR discrete seat on a
// pin-board, so most pins resolve within that board by footprint arithmetic
// alone. A rotated two-terminal part (resistor / LED) is different: it is a
// free two-ends device — pin 1 anchors in ANY hole (grid OR power rail) and
// pin 2 is a `{dx, dy}` bend from it, resolved GEOMETRICALLY against whatever
// board lies under it. So BOTH leads can reach rails, and pulling a strip away
// from under either the anchor or the bent lead leaves the part where it was,
// with that leg floating.

import { footprintOffsets, partDef } from "../catalog/index.js";
import {
  allPinHoles,
  coveredHoles,
  dipRows,
  flippedPin,
  seatsWide,
  veroCoveredHoles,
  veroPinHoles,
} from "./footprints.js";
import {
  boardSize,
  formatAddress,
  isVeroHoleId,
  shiftVeroHole,
  holeAt,
  holePosition,
  holesNear,
  parseAddress,
  parseHole,
  ROTATIONS,
  rotateOffset,
} from "./breadboard.js";

// A discrete anchors in ANY row. So does a chip, as far as parsing goes: pin
// 1's row is the seat (footprints.js `dipRows` says which rows a given package
// may take — e, or b–e for a 600-mil part at its true width).
const GRID_ANCHOR_RE = /^([a-j])([1-9]\d*)$/;
// A TURNED two-terminal part (resistor / LED) anchors pin 1 in ANY hole — a
// grid row OR a power rail — so BOTH leads can reach rails (e.g. a resistor
// bridging two rails). The linear footprint form stays GRID_ANCHOR_RE only:
// its offsets are grid-column arithmetic that the rail's grouped lattice can't
// honour. Pin 1 being a real hole on its own board is all the turned form needs.
const LEAD_ANCHOR_RE = /^([a-j]|[+-])([1-9]\d*)$/;
// A veroboard hole (`C14`, breadboard.js) takes every anchor a grid hole
// does: a part on a stripboard is seated by the same footprints, stated on
// its lattice (footprints.js `veroPinHoles`, `shiftVeroHole` here).
const gridAnchor = (anchor) =>
  GRID_ANCHOR_RE.test(anchor) || isVeroHoleId(anchor);
const leadAnchor = (anchor) =>
  LEAD_ANCHOR_RE.test(anchor) || isVeroHoleId(anchor);

/**
 * A `def.can` part's pin-1 offset from the shape's own centre, at rot 0 —
 * bottom-left of its `width` × `height` rectangle. The one fact both
 * `partPinHoles` (below) and a rotate-in-place (`desk-doc.js`) need to
 * convert between "pin 1's hole" and "the shape's centre".
 */
export function canCornerOffset(def) {
  return { dx: -def.can.width / 2, dy: def.can.height / 2 };
}

/**
 * The seated hole of every pin of a board part (chip OR discrete):
 * `ref` + `anchor` → derived `[{ pin, hole }]`, or null when the ref is
 * unknown, isn't a board-seated part, or the anchor doesn't fit its
 * footprint (chips: row e, or b–e for a 600-mil part; discretes: any grid row).
 * Whether each hole
 * exists on a given board type is the caller's check — this is pure
 * footprint arithmetic.
 *
 * A rotated part's free lead has no hole on the part's own board: its entry
 * is `{ pin, offset }` instead of `{ pin, hole }`, since where it lands
 * depends on the desk. Use partPinAddresses() to resolve it.
 */
export function partPinHoles(ref, anchor, params) {
  const def = partDef(ref);
  if (!def || typeof anchor !== "string") return null;
  if (def.package) {
    // DIP chip straddling the trench, at the width its anchor row says — or
    // across a veroboard's strips, at its package's own width.
    let seated;
    if (isVeroHoleId(anchor)) {
      seated = veroPinHoles(def.package, anchor);
      if (seated.length === 0) return null;
    } else {
      const m = GRID_ANCHOR_RE.exec(anchor);
      const wide = seatsWide(params);
      if (!m || !dipRows(def.package, m[1], wide)) return null;
      seated = allPinHoles(def.package, Number(m[2]), m[1], wide).map(
        ({ pin, row, col }) => ({ pin, hole: `${row}${col}` }),
      );
    }
    if (params?.rot !== 180) return seated;
    // Flipped 180°: a DIP's footprint maps onto ITSELF (same two rows, same
    // columns, at either width), so only the pin numbering turns half a lap — pin 1 lands where
    // the opposite corner pin sat (footprints.js's flippedPin, its own inverse).
    const holeOfPin = new Map(seated.map((s) => [s.pin, s.hole]));
    return seated.map(({ pin }) => ({
      pin,
      hole: holeOfPin.get(flippedPin(def.package, pin)),
    }));
  }
  if (def.can) {
    // A rigid multi-corner shape (an oscillator can): pin 1 anchors in ANY
    // grid row, like a linear discrete; the other 3 corners are {dx, dy}
    // offsets from it, rotated to the part's current quarter-turn — resolved
    // geometrically (partPinAddresses), exactly like a rotated LED/resistor's
    // bent lead, so a corner may legally resolve to nothing (the trench gap)
    // without disturbing the other three.
    if (!gridAnchor(anchor)) return null;
    const rot = ROTATIONS.includes(params?.rot) ? params.rot : 0;
    const { width: w, height: h } = def.can;
    const rel = [
      { dx: w, dy: 0 },
      { dx: w, dy: -h },
      { dx: 0, dy: -h },
    ].map((v) => rotateOffset(v, rot));
    return [
      { pin: def.pins[0].n, hole: anchor },
      { pin: def.pins[1].n, offset: rel[0] },
      { pin: def.pins[2].n, offset: rel[1] },
      { pin: def.pins[3].n, offset: rel[2] },
    ];
  }
  if (def.footprint) {
    // A TO-92 whose legs are BENT (model/free-legs.js): pin 1 in ANY hole, a
    // grid row or a rail, as a turned lead's is; pins 2 and 3 offsets from
    // it, each resolved against whatever strip lies under it.
    if (Array.isArray(params?.legs) && def.freeLegs?.(params)) {
      const legs = params.legs;
      const real = (v) => v && Number.isFinite(v.dx) && Number.isFinite(v.dy);
      if (legs.length !== 2 || !legs.every(real)) return null;
      if (!leadAnchor(anchor)) return null;
      return [
        { pin: def.pins[0].n, hole: anchor },
        { pin: def.pins[1].n, offset: { dx: legs[0].dx, dy: legs[0].dy } },
        { pin: def.pins[2].n, offset: { dx: legs[1].dx, dy: legs[1].dy } },
      ];
    }
    // Rotated (vertical) two-free-ends form: pin 1 at the anchor hole, pin 2
    // bent to a {dx, dy} offset instead of a footprint offset. The bend may
    // reach off this board entirely, so it stays geometry here. Pin 1 may
    // anchor on a rail as freely as on the grid (LEAD_ANCHOR_RE), so both leads
    // can land on rails — whether each anchor/lead hole actually EXISTS is the
    // caller's parseHole check (partPinAddresses), as ever.
    if (def.rotatable && params?.rot === 90) {
      const end = params.end;
      // Finite, not integer: a bend is the vector between two HOLES, and the
      // vertical geometry is measured (board-types.js) — row a to a dovetailed
      // rail's nearest row is 2.76. Demanding whole pitches here refused the
      // very reach this branch exists for. `normalizeLeadOffset` is what puts
      // it on the 0.01 grid; this only asks that it be a real vector.
      if (!end || !Number.isFinite(end.dx) || !Number.isFinite(end.dy)) {
        return null;
      }
      if (!leadAnchor(anchor)) return null;
      return [
        { pin: def.pins[0].n, hole: anchor },
        { pin: def.pins[1].n, offset: { dx: end.dx, dy: end.dy } },
      ];
    }
    // A rigid two-legged body turned a quarter lap (or more) about pin 1
    // (`def.turns` — the speaker): pin 1 seats in its hole, every other pin
    // is its footprint offset turned the same way, resolved against whatever
    // strip is under it (as a can's corners are).
    const turn = ROTATIONS.includes(params?.rot) ? params.rot : 0;
    if (def.turns && turn !== 0) {
      if (!gridAnchor(anchor)) return null;
      return footprintOffsets(def, params).map((dx, i) =>
        i === 0
          ? { pin: def.pins[0].n, hole: anchor }
          : { pin: def.pins[i].n, offset: rotateOffset({ dx, dy: 0 }, turn) },
      );
    }
    // Linear discrete along one grid row (or one row of a veroboard's holes,
    // across its strips).
    const vero = isVeroHoleId(anchor);
    const m = vero ? null : GRID_ANCHOR_RE.exec(anchor);
    if (!vero && !m) return null;
    const holeOf = (offset) =>
      vero
        ? shiftVeroHole(anchor, offset, 0)
        : `${m[1]}${Number(m[2]) + offset}`;
    // Turned end-for-end (`reversible`, params.rot 180): the SAME holes, in the
    // same order, hosting the pin numbering backwards — the linear form of the
    // DIP half lap above. It maps onto itself only because a reversible def's
    // offsets are palindromic (asserted in tests/parts-catalog.test.js), so
    // there is no second geometry to derive: pins[i] and pins[n-1-i] simply
    // trade places.
    const flipped = def.reversible === true && params?.rot === 180;
    const last = def.pins.length - 1;
    const seated = footprintOffsets(def, params).map((offset, i) => ({
      pin: def.pins[flipped ? last - i : i].n,
      hole: holeOf(offset),
    }));
    return seated.every((s) => s.hole) ? seated : null;
  }
  return null; // a PSU has terminals, not board pins
}

/**
 * The holes a seated part's BODY stands over without a pin in them — the three
 * rows between a 600-mil chip's pins at its true width (footprints.js
 * `coveredHoles`; `params` says whether row e is the e/i seat),
 * and nothing for any other part or seat. `[]` (never null) when there is
 * nothing to cover, an unknown ref included: a body that covers nothing claims
 * nothing.
 * @returns {string[]} hole ids on the part's own board
 */
export function partCoverHoles(ref, anchor, params = null) {
  const def = partDef(ref);
  if (!def?.package || typeof anchor !== "string") return [];
  if (isVeroHoleId(anchor)) return veroCoveredHoles(def.package, anchor);
  const m = GRID_ANCHOR_RE.exec(anchor);
  if (!m) return [];
  return coveredHoles(def.package, Number(m[2]), m[1], seatsWide(params)).map(
    ({ row, col }) => `${row}${col}`,
  );
}

/**
 * `partCoverHoles` as DESK ADDRESSES on the part's own board — only the holes
 * that board really has.
 * @param {{ boards: Array }} doc
 * @param {{ ref:string, board:string, anchor:string, params?:object }} comp
 * @returns {string[]}
 */
export function partCoverAddresses(doc, comp) {
  const board = (doc?.boards ?? []).find((b) => b.id === comp?.board);
  if (!board) return [];
  return partCoverHoles(comp.ref, comp.anchor, comp.params)
    .filter((hole) => {
      try {
        return parseHole(board.type, hole) !== null;
      } catch {
        return false; // a junk board type covers nothing
      }
    })
    .map((hole) => formatAddress(board.id, hole));
}

/**
 * The board + hole under a world point, with the hole's exact world position,
 * or null over bare desk (or over a board but between holes).
 *
 * The ONE authority for "what is under this point" — hover, the wire tool,
 * part drags, and addressAtWorld() all come through here, so none of them can
 * drift apart on the boundary cases.
 *
 * Boards never overlap, but mated strips meet FLUSH and the bounds test is
 * inclusive on every edge, so a point on a shared seam falls inside BOTH
 * neighbours' boxes. A box hit with no hole near enough therefore has to keep
 * looking: giving up on the first box would make the answer depend on board
 * order and hide the neighbour's holes along every dovetail.
 *
 * @param {Array<{id:string,type:string,x:number,y:number,rot?:number}>} boards
 * @returns {{board:object, hole:string, x:number, y:number}|null}
 */
export function holeAtWorld(boards, x, y) {
  for (const board of boards ?? []) {
    const rot = board.rot ?? 0;
    let size;
    try {
      size = boardSize(board.type, rot);
    } catch {
      continue; // a foreign/junk board type never owns a hole
    }
    if (x < board.x || y < board.y) continue;
    if (x > board.x + size.width || y > board.y + size.height) continue;
    const hole = holeAt(board.type, x - board.x, y - board.y, rot);
    if (!hole) continue;
    const pos = holePosition(board.type, hole, rot);
    if (!pos) continue;
    return { board, hole, x: board.x + pos.x, y: board.y + pos.y };
  }
  return null;
}

/**
 * Every board hole within `radius` of a world point, as
 * `[{ board, hole, x, y, dist }]` (unordered across boards) — `holeAtWorld`
 * widened the way `holesNear` widens `holeAt`. A board is asked only when the
 * point lies within `radius` of its rect, so a hole just past a strip's edge
 * is still in reach of a cursor that has slid off it.
 *
 * @param {Array<{id:string,type:string,x:number,y:number,rot?:number}>} boards
 * @returns {Array<{board:object, hole:string, x:number, y:number, dist:number}>}
 */
export function holesNearWorld(boards, x, y, radius) {
  const out = [];
  for (const board of boards ?? []) {
    const rot = board.rot ?? 0;
    let size;
    try {
      size = boardSize(board.type, rot);
    } catch {
      continue; // a foreign/junk board type never owns a hole
    }
    if (x < board.x - radius || y < board.y - radius) continue;
    if (x > board.x + size.width + radius) continue;
    if (y > board.y + size.height + radius) continue;
    for (const { hole, dist } of holesNear(
      board.type,
      x - board.x,
      y - board.y,
      radius,
      rot,
    )) {
      const pos = holePosition(board.type, hole, rot);
      if (!pos) continue;
      out.push({ board, hole, x: board.x + pos.x, y: board.y + pos.y, dist });
    }
  }
  return out;
}

/**
 * The desk address of the hole at a world point, or null over bare desk.
 *
 * @param {Array<{id:string,type:string,x:number,y:number}>} boards
 */
export function addressAtWorld(boards, x, y) {
  const hit = holeAtWorld(boards, x, y);
  return hit ? formatAddress(hit.board.id, hit.hole) : null;
}

/**
 * Every pin of a seated part as a DESK ADDRESS, resolving a rotated part's
 * free lead against whatever board lies under its bend.
 *
 * Returns `[{ pin, address }]` where `address` is null for a lead that
 * currently touches nothing — a floating leg, which is a legal state, not an
 * error. Returns null only when the part itself can't be resolved (unknown
 * ref, missing board, anchor off the footprint).
 *
 * @param {{ boards: Array }} doc
 * @param {{ ref:string, board:string, anchor:string, params?:object }} comp
 */
export function partPinAddresses(doc, comp) {
  const boards = doc?.boards ?? [];
  const board = boards.find((b) => b.id === comp?.board);
  if (!board) return null;
  const pins = partPinHoles(comp.ref, comp.anchor, comp.params);
  if (!pins) return null;

  // The anchor's world position — the origin every bend is measured from.
  // Guarded like addressAtWorld/worldOfAddress: a junk board type resolves to
  // nothing rather than throwing, so one corrupt entry can't take out a whole
  // occupancy rebuild.
  let anchorWorld = null;
  if (pins.some((p) => p.offset)) {
    let pos = null;
    try {
      pos = holePosition(board.type, comp.anchor, board.rot ?? 0);
    } catch {
      return null;
    }
    if (!pos) return null;
    anchorWorld = { x: board.x + pos.x, y: board.y + pos.y };
  }

  return pins.map(({ pin, hole, offset }) => {
    if (hole != null) {
      // On the part's own board — but still only real if the type has it.
      if (!parseHole(board.type, hole)) return { pin, address: null };
      return { pin, address: formatAddress(board.id, hole) };
    }
    return {
      pin,
      address: addressAtWorld(
        boards,
        anchorWorld.x + offset.dx,
        anchorWorld.y + offset.dy,
      ),
    };
  });
}

/**
 * Build the address → occupant index for a document. Occupants:
 *   { kind: "pin", componentId, pin }   — a seated chip pin
 *   { kind: "body", componentId }       — a hole a chip's body stands over
 *                                         (`partCoverAddresses`: rows e–g
 *                                         under a 600-mil part)
 *   { kind: "wire", wireId, end }       — a wire end ("from" | "to")
 *   { kind: "signal", signalId }        — a planted signal flag's POINT
 *   { kind: "tag", elementId, key }     — a planted Output/Input tag's point
 *
 * Unresolvable entries (unknown ref, malformed anchor/address) contribute
 * nothing — normalizeDocument drops them on load anyway.
 *
 * ORDER MATTERS, and it is the inverse of the loader's. normalizeDocument
 * claims a hole PINS → WIRES → FLAGS and keeps the FIRST; this map is
 * last-writer-wins, so the same precedence is spelled backwards: tags and
 * flags first, then wires, then pins. A body's cover goes in before all of
 * them: it holds no lead, so any lead a hand-edited file left under one is what
 * the hole reads as. A normalized document never collides, but this also
 * runs against live mid-mutation documents, and of the two ways a collision
 * could read, a flag masking a WIRE end is the harmful one — canReendWire
 * would then refuse to move that wire's own end, wedging it for good, where
 * the other direction merely permits a placement.
 *
 * @param {{ boards: Array, components: Array, wires: Array, signals: Array }} doc
 * @returns {Map<string, object>}
 */
export function buildOccupancy(doc) {
  const map = new Map();
  // The holes a chip's body covers: nothing may be plugged into one.
  for (const comp of doc.components ?? []) {
    if (comp?.kind !== "chip" && comp?.kind !== "discrete") continue;
    for (const address of partCoverAddresses(doc, comp)) {
      map.set(address, { kind: "body", componentId: comp.id });
    }
  }
  // A planted flag's point is a lead like any other — one hole, one lead.
  for (const sig of doc.signals ?? []) {
    const address = sig?.flag?.anchor;
    if (typeof address === "string") {
      map.set(address, { kind: "signal", signalId: sig.id });
    }
  }
  // An integration element's tags are flags by another name: each planted
  // point is one lead in one hole.
  for (const element of doc.integrations ?? []) {
    for (const [key, tag] of Object.entries(element?.tags ?? {})) {
      if (typeof tag?.anchor === "string") {
        map.set(tag.anchor, { kind: "tag", elementId: element.id, key });
      }
    }
  }
  // PSU terminals are connection POINTS like holes: free until a wire lands.
  for (const wire of doc.wires ?? []) {
    if (!wire || typeof wire !== "object") continue;
    for (const end of ["from", "to"]) {
      if (typeof wire[end] !== "string") continue;
      map.set(wire[end], { kind: "wire", wireId: wire.id, end });
    }
  }
  // Pins last, so they win — the loader's PINS-first, spelled backwards.
  for (const comp of doc.components ?? []) {
    if (!comp || (comp.kind !== "chip" && comp.kind !== "discrete")) continue;
    const pins = partPinAddresses(doc, comp);
    if (!pins) continue;
    for (const { pin, address } of pins) {
      // A floating lead occupies nothing.
      if (address == null) continue;
      map.set(address, { kind: "pin", componentId: comp.id, pin });
    }
  }
  return map;
}

/**
 * Does `address` name a real connection point — a hole on an existing
 * board, or a terminal of an existing desk component (psu1.+)?
 */
export function isRealPoint(doc, address) {
  const parsed = parseAddress(address);
  if (!parsed) return false;
  const board = (doc.boards ?? []).find((b) => b.id === parsed.boardId);
  if (board) return parseHole(board.type, parsed.hole) !== null;
  const comp = (doc.components ?? []).find((c) => c.id === parsed.boardId);
  if (comp) {
    const def = partDef(comp.ref);
    return Boolean(def?.terminals?.some((t) => t.id === parsed.hole));
  }
  return false;
}

/**
 * Is `address` a real, unoccupied connection point (hole or terminal)?
 *
 * @param {{ boards: Array, components: Array, wires: Array }} doc
 * @param {string} address - e.g. "bb1.j5" or "psu1.+"
 */
export function isFreeHole(doc, address) {
  if (!isRealPoint(doc, address)) return false;
  return !buildOccupancy(doc).has(address);
}

/**
 * May a wire connect these two holes? Both ends must be free, real holes,
 * and distinct (one hole holds one lead — a wire may still join two holes
 * of the SAME internal node, which is harmless and real boards do it).
 */
export function canPlaceWire(doc, from, to) {
  return from !== to && isFreeHole(doc, from) && isFreeHole(doc, to);
}

/**
 * May wire `wireId`'s `end` ("from" | "to") be re-addressed to `address`? Like
 * `canPlaceWire`, but for MOVING one end of an existing wire: the target must
 * be a real point, distinct from the wire's OTHER (anchored) end, and free —
 * ignoring the moving wire's own two endpoints (so it slides off its old hole
 * without colliding with itself).
 *
 * @param {{ boards: Array, components: Array, wires: Array }} doc
 * @param {string} wireId
 * @param {"from"|"to"} end
 * @param {string} address
 */
export function canReendWire(doc, wireId, end, address, occupancy = null) {
  const wire = (doc.wires ?? []).find((w) => w && w.id === wireId);
  if (!wire || (end !== "from" && end !== "to")) return false;
  const other = end === "from" ? wire.to : wire.from;
  if (address === other || !isRealPoint(doc, address)) return false;
  const occupant = (occupancy ?? buildOccupancy(doc)).get(address);
  return !occupant || (occupant.kind === "wire" && occupant.wireId === wireId);
}

/**
 * May signal `signalId`'s flag plug into `address`? The point must be free —
 * ignoring this signal's OWN current claim, so a flag slides off its old hole
 * without colliding with itself. That is `canReendWire`'s argument one item
 * over, and it is what lets ONE method serve both planting and moving.
 *
 * Deliberately does NOT test that the address is a board hole: the caller
 * (DeskDoc.canPlaceSignalFlag) owns that half, because "a flag plugs into a
 * breadboard, not into a PSU terminal" is a rule about signals, not about
 * collisions, and this file is the collision authority alone.
 *
 * @param {{ boards: Array, components: Array, wires: Array, signals: Array }} doc
 * @param {string} signalId
 * @param {string} address
 */
export function canPlaceFlag(doc, signalId, address) {
  const occupant = buildOccupancy(doc).get(address);
  return (
    !occupant || (occupant.kind === "signal" && occupant.signalId === signalId)
  );
}

/**
 * May element `elementId`'s tag `key` plug into `address`? `canPlaceFlag`'s
 * rule one item over: free, ignoring the tag's OWN current claim, so one
 * method serves planting and moving alike. The board-hole half is the
 * caller's (DeskDoc.canPlaceIntegrationTag), for the same reason as a flag's.
 *
 * @param {{ integrations?: Array }} doc
 * @param {string} elementId
 * @param {string} key
 * @param {string} address
 */
export function canPlaceTag(doc, elementId, key, address) {
  const occupant = buildOccupancy(doc).get(address);
  return (
    !occupant ||
    (occupant.kind === "tag" &&
      occupant.elementId === elementId &&
      occupant.key === key)
  );
}

/**
 * May wire `wireId` move RIGIDLY so its two ends land on `from` and `to` at
 * once (the drag-the-whole-wire gesture)? Both targets must be real, distinct
 * points, and free — ignoring the moving wire's OWN two endpoints, since it
 * slides off both old holes simultaneously.
 *
 * @param {{ boards: Array, components: Array, wires: Array }} doc
 * @param {string} wireId
 * @param {string} from
 * @param {string} to
 */
export function canMoveWire(doc, wireId, from, to, index = null) {
  if (from === to) return false;
  if (!isRealPoint(doc, from) || !isRealPoint(doc, to)) return false;
  const occupancy = index ?? buildOccupancy(doc);
  for (const address of [from, to]) {
    const occupant = occupancy.get(address);
    if (occupant && !(occupant.kind === "wire" && occupant.wireId === wireId)) {
      return false;
    }
  }
  return true;
}

/**
 * The world position (pitch units) of an address's hole, or null when the
 * board is gone or the hole doesn't exist on its type.
 *
 * @param {Array<{id:string,type:string,x:number,y:number}>} boards
 */
export function worldOfAddress(boards, address) {
  const parsed = parseAddress(address);
  if (!parsed) return null;
  const board = (boards ?? []).find((b) => b.id === parsed.boardId);
  if (!board) return null;
  let pos = null;
  try {
    pos = holePosition(board.type, parsed.hole, board.rot ?? 0);
  } catch {
    return null; // junk board type
  }
  return pos ? { x: board.x + pos.x, y: board.y + pos.y } : null;
}

/** Where pin `i` of a seated part's lead stands on the desk — its hole, or
    for a bent lead the anchor plus its bend, whether or not a hole is there. */
function leadWorld(boards, comp, i) {
  const pin = partPinHoles(comp.ref, comp.anchor, comp.params)?.[i];
  const anchor = worldOfAddress(boards, formatAddress(comp.board, comp.anchor));
  if (!pin || !anchor) return null;
  if (pin.offset) return { x: anchor.x + pin.offset.dx, y: anchor.y + pin.offset.dy }; // prettier-ignore
  return worldOfAddress(boards, formatAddress(comp.board, pin.hole));
}

/**
 * May a board part (chip or discrete) seat here? True when the board exists,
 * the anchor fits the footprint (chips row e, or d at a 600-mil part's true
 * width; discretes any grid row), EVERY lead lands in a real hole, and every
 * one of those holes is free — and so is every hole its BODY would cover (rows
 * e–g under a wide chip: a wire, a flag or another part's lead there would be
 * under the plastic) — ignoring the pins and body of `ignoreId` (a part may
 * move within its own footprint).
 *
 * Note a rotated part's free lead may legally land on a DIFFERENT board (a
 * power rail alongside the pin-board); it must still land in a real hole.
 * Floating is a state a part falls into when a strip is moved or deleted out
 * from under it — never one you can deliberately place into. `floating` names
 * the pins that float ALREADY: a part carried by its other lead (riding a
 * moved chip) keeps such a lead where it is, still in mid-air, and the move
 * is not to be refused for a state the part was in before it.
 *
 * `occupancy` is a prebuilt `buildOccupancy(doc)` map for a caller checking MANY
 * placements against ONE document — this function otherwise rebuilds the whole
 * index per call, which a live drag over a cluster of parts would pay for once
 * per member per pointer sample. It must be the index OF `doc`; the hoist is a
 * cache, never a second opinion.
 *
 * @param {{ boards: Array, components: Array }} doc
 * @param {{ ref: string, board: string, anchor: string, ignoreId?: string|null,
 *   occupancy?: Map<string, object>|null, floating?: Set<number>|null }} opts
 */
export function canPlacePart(
  doc,
  {
    ref,
    board: boardId,
    anchor,
    params = null,
    ignoreId = null,
    occupancy = null,
    floating = null,
  },
) {
  const boards = doc.boards ?? [];
  if (!boards.some((b) => b.id === boardId)) return false;
  const def = partDef(ref);
  const pins = partPinAddresses(doc, { ref, board: boardId, anchor, params });
  if (!pins) return false;
  // A lead in mid-air — unless it was there already (`floating`).
  if (pins.some((p) => p.address == null && !floating?.has(p.pin))) {
    return false;
  }
  const seated = pins.filter((p) => p.address != null);
  // A rotated part's two ends — a bent TO-92's three legs — must be DISTINCT
  // holes (a wire could join a node's own holes; a device with two leads in
  // one hole is nonsense).
  if (
    (params?.rot === 90 || Array.isArray(params?.legs)) &&
    new Set(seated.map((p) => p.address)).size !== seated.length
  ) {
    return false;
  }
  // Two-terminal parts with a declared minimum lead span (a resistor's body
  // needs room) must keep their ends that far apart — any angle, any distance
  // beyond it, so a lead can reach the far rail. A floating end is where its
  // bend puts it.
  if (def?.minSpan && pins.length === 2) {
    const at = (p, i) =>
      p.address != null
        ? worldOfAddress(boards, p.address)
        : leadWorld(boards, { ref, board: boardId, anchor, params }, i);
    const pa = at(pins[0], 0);
    const pb = at(pins[1], 1);
    if (!pa || !pb) return false;
    // With a hair to spare: row y's live on a 0.01 grid, which binary floats
    // do not (3.51 − 1.51 is 1.9999999999999998), so a diode laid exactly its
    // minimum across rows h→j read as too short.
    if (Math.hypot(pb.x - pa.x, pb.y - pa.y) < def.minSpan - 1e-6) return false;
  }
  const index = occupancy ?? buildOccupancy(doc);
  const covered = partCoverAddresses(doc, {
    ref,
    board: boardId,
    anchor,
    params,
  });
  for (const address of [...seated.map((p) => p.address), ...covered]) {
    const occupant = index.get(address);
    if (occupant && occupant.componentId !== ignoreId) return false;
  }
  return true;
}
